import ExcelJS from 'exceljs';
import format from 'date-fns/format';
import { formatOrderDate } from 'src/sections/shopping_lists/utils/exportShoppingListsToExcel';
import {
  ShoppingListResponseItem,
} from 'src/types/faviriteProducts/getShoppingList';
import {
  SHOPPING_LIST_EXCEL_STYLES,
  buildShoppingListExcelWorksheet,
  downloadExcelWorkbook,
} from 'src/utils/shoppingListExcelWorksheetBuilder';

export const SHOPPING_LISTS_EXPORT_SHEET_NAME = 'Shopping Lists';
export const SHOPPING_LIST_EXPORT_SHEET_NAME = 'Shopping List';

export const SHOPPING_LIST_EXPORT_COLUMNS = [
  'Type',
  'Item Number',
  'Brand Name',
  'Product Name',
  'Catalog',
  'Supplier Name',
  'Unit',
  'Case PK',
  'Price',
  'Par',
  'On Hand',
  'Order',
  'Sort',
] as const;

export interface ShoppingListExportSection {
  listName: string;
  items: ShoppingListResponseItem[];
}

type ExportCellValue = string | number;

interface StyledExportRow {
  values: ExportCellValue[];
  kind: 'title' | 'date' | 'header' | 'data' | 'empty' | 'total' | 'spacer';
}

export const EMPTY_FOLDER_MESSAGE = 'There are no products in this folder.';

export type ShoppingListExportTranslateFn = (
  key: string,
  options?: Record<string, unknown>
) => string;

export const defaultShoppingListExportTranslate: ShoppingListExportTranslateFn = (
  key,
  options
) => {
  if (!options) {
    return key;
  }

  return Object.entries(options).reduce(
    (result, [name, value]) => result.replace(new RegExp(`{{\\s*${name}\\s*}}`, 'g'), String(value)),
    key
  );
};

/**
 * Fixed widths tuned for US Letter landscape print (all columns on one page width).
 * Type narrow; Product Name widest; Catalog/Supplier medium; remaining columns compact.
 */
export const SHOPPING_LIST_EXPORT_COLUMN_WIDTHS = [
  5, // Type
  9, // Item Number
  9, // Brand Name
  20, // Product Name
  11, // Catalog
  11, // Supplier Name
  5, // Unit
  7, // Case PK
  7, // Price
  4, // Par
  7, // On Hand
  6, // Order
  4, // Sort
] as const;

const toCellValue = (value: string | number | null | undefined): ExportCellValue => {
  if (value == null) {
    return '';
  }
  if (typeof value === 'number') {
    return value;
  }
  return value;
};

const formatExportPrice = (price: number | null | undefined): string => {
  if (price == null || Number.isNaN(price)) {
    return '';
  }
  return `$${Number(price).toFixed(2)}`;
};

export const mapShoppingListItemToExportRow = (
  item: ShoppingListResponseItem
): ExportCellValue[] => [
  '',
  toCellValue(item.itemNum?.trim()),
  toCellValue(item.brandName?.trim()),
  toCellValue(item.productName?.trim()),
  toCellValue(item.catalog?.trim()),
  toCellValue(item.supplierName?.trim()),
  toCellValue(item.uom?.trim()),
  toCellValue(item.casePack),
  formatExportPrice(item.price),
  toCellValue(item.par),
  '',
  formatOrderDate(item.lastPurchaseDate),
  toCellValue(item.sortPriority),
];

export const getShoppingListExportFileName = (
  listName: string,
  date: Date = new Date()
): string => {
  const sanitizedListName = listName.trim();
  return `${sanitizedListName}_${format(date, 'MM_dd_yyyy')}`;
};

export const getShoppingListsExportFileName = (date: Date = new Date()): string =>
  `Shopping_Lists_${format(date, 'MM_dd_yyyy')}`;

export const getShoppingListsExportSheetName = (
  sections: ShoppingListExportSection[],
  t: ShoppingListExportTranslateFn = defaultShoppingListExportTranslate
): string => {
  if (sections.length === 1) {
    return t(SHOPPING_LIST_EXPORT_SHEET_NAME);
  }

  return t(SHOPPING_LISTS_EXPORT_SHEET_NAME);
};

export const getShoppingListsExportFileNameForSections = (
  sections: ShoppingListExportSection[],
  date: Date = new Date()
): string => {
  if (sections.length === 1) {
    return getShoppingListExportFileName(sections[0].listName, date);
  }

  return getShoppingListsExportFileName(date);
};

export const getShoppingListsExportDisplayDate = (date: Date = new Date()): string =>
  format(date, 'M/d/yyyy');

export const buildShoppingListsExportStyledRows = (
  sections: ShoppingListExportSection[],
  exportDate: Date = new Date(),
  t: ShoppingListExportTranslateFn = defaultShoppingListExportTranslate
): StyledExportRow[] => {
  const displayDate = getShoppingListsExportDisplayDate(exportDate);
  const rows: StyledExportRow[] = [];

  sections.forEach((section, index) => {
    if (index > 0) {
      rows.push({ values: [], kind: 'spacer' });
    }

    rows.push({
      values: [t('Shopping List: {{listName}}', { listName: section.listName })],
      kind: 'title',
    });
    rows.push({
      values: [t('Date: {{date}}', { date: displayDate })],
      kind: 'date',
    });
    rows.push({
      values: SHOPPING_LIST_EXPORT_COLUMNS.map((column) => t(column)),
      kind: 'header',
    });

    if (!section.items.length) {
      rows.push({
        values: [t(EMPTY_FOLDER_MESSAGE)],
        kind: 'empty',
      });
      return;
    }

    section.items.forEach((item) => {
      rows.push({
        values: mapShoppingListItemToExportRow(item),
        kind: 'data',
      });
    });

    rows.push({
      values: [t('Total Products: {{count}}', { count: section.items.length })],
      kind: 'total',
    });
  });

  return rows;
};

/** @deprecated Prefer buildShoppingListsExportStyledRows for styled exports */
export const buildShoppingListsExportRows = (
  sections: ShoppingListExportSection[],
  exportDate: Date = new Date(),
  t: ShoppingListExportTranslateFn = defaultShoppingListExportTranslate
): ExportCellValue[][] =>
  buildShoppingListsExportStyledRows(sections, exportDate, t).map((row) => row.values);

export const buildShoppingListsExportColumnWidths = (): { wch: number }[] =>
  SHOPPING_LIST_EXPORT_COLUMN_WIDTHS.map((wch) => ({ wch }));

const styleForKind = (kind: StyledExportRow['kind']) => {
  switch (kind) {
    case 'title':
      return SHOPPING_LIST_EXCEL_STYLES.title;
    case 'date':
      return SHOPPING_LIST_EXCEL_STYLES.date;
    case 'header':
      return SHOPPING_LIST_EXCEL_STYLES.header;
    case 'total':
      return SHOPPING_LIST_EXCEL_STYLES.total;
    case 'data':
      return SHOPPING_LIST_EXCEL_STYLES.data;
    case 'empty':
      return SHOPPING_LIST_EXCEL_STYLES.empty;
    default:
      return null;
  }
};

const isFullWidthMergedRow = (kind: StyledExportRow['kind']): boolean =>
  kind === 'title' || kind === 'empty' || kind === 'total';

export const exportShoppingListsToExcel = async (
  sections: ShoppingListExportSection[],
  t: ShoppingListExportTranslateFn = defaultShoppingListExportTranslate,
  exportDate: Date = new Date()
): Promise<void> => {
  if (sections.length === 0) {
    return;
  }

  const styledRows = buildShoppingListsExportStyledRows(sections, exportDate, t);
  const sheetName = getShoppingListsExportSheetName(sections, t);
  const fileName = getShoppingListsExportFileNameForSections(sections, exportDate);
  const workbook = new ExcelJS.Workbook();

  buildShoppingListExcelWorksheet(workbook, sheetName, styledRows, {
    columnCount: SHOPPING_LIST_EXPORT_COLUMNS.length,
    columnWidths: SHOPPING_LIST_EXPORT_COLUMN_WIDTHS,
    isFullWidthMergedRow,
    styleForKind,
  });

  await downloadExcelWorkbook(workbook, fileName);
};
